import { Box } from '@chakra-ui/react';
import VTextarea from '@voly/ui-components/src/v2/VTextarea';
import VTooltip from '@voly/ui-components/src/v2/VTooltip';
import { ChangeEvent, KeyboardEvent, ReactElement, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import VFormFieldHorizontal from '@voly/ui-components/src/v2/VFormFieldHorizontal';
import PointFieldName from './PointFieldName';
import FieldLockedIcon from '../FieldLockedIcon';

interface PointFieldTextProps {
  field: TCustomField;
  value: string;
  readOnly: boolean;
  onSave: (value: string) => void;
}

const PointFieldText = ({ field, value, readOnly, onSave }: PointFieldTextProps): ReactElement => {
  const [textValue, setTextValue] = useState(value);
  const [savedValue, setSavedValue] = useState(value);

  if (savedValue !== value) {
    setSavedValue(value);
    setTextValue(value);
  }

  const commit = (): void => {
    const trimmedTextValue = textValue.trim();

    if (trimmedTextValue === value) {
      return;
    }

    onSave(trimmedTextValue);
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setTextValue(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.currentTarget.blur();
  };

  return (
    <VFormFieldHorizontal
      data-testid="point-custom-field-row"
      size="sm"
      label={<PointFieldName field={field} />}
      helperText={field.description}
      errorText={undefined}
      invalid={undefined}
      readOnly={readOnly}
      disabled={field.lockedValue}
    >
      <VTooltip content={translate('locked_by_admin')} disabled={!field.lockedValue}>
        <Box
          data-testid="point-custom-field-control"
          position="relative"
          width="100%"
          cursor={field.lockedValue ? 'not-allowed' : undefined}
        >
          <VTextarea
            autoresize
            rows={1}
            width="100%"
            value={textValue}
            placeholder={field.lockedValue ? '' : translate('enter_text_here')}
            pointerEvents={field.lockedValue ? 'none' : undefined}
            paddingStart={field.lockedValue ? '9' : undefined}
            onChange={handleChange}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            size="sm"
          />

          {field.lockedValue ? (
            <FieldLockedIcon position="absolute" insetStart={'3'} top={'2'} />
          ) : undefined}
        </Box>
      </VTooltip>
    </VFormFieldHorizontal>
  );
};

export default PointFieldText;
